import { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import HomeIcon from '@mui/icons-material/Home';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { useFormikContext } from 'formik';
import { PAGE_ICONS, PAGE_ROUTES } from '../../_const';
import { DELEGATE_FIELD_KEYS, DELEGATE_FIELDS } from './_config';
import ReviewAndSubmitView from 'pages/AnnualUserAccessReview/layouts/ReviewAndSubmitView';

/**
 * Display information about the form before submission
 * @param {boolean} props.submissionStatus indicator for whether the form has been submitted
 * @returns {ReactElement} component displaying information about the errors or the submission details
 */
const ReviewAndSubmit = ({ submissionStatus }) => {
	const { values, isSubmitting, errors } = useFormikContext();
	const history = useHistory();

	const isSubmittingState = isSubmitting;
	const isSubmittedState = submissionStatus?.submitted;
	const isSubmissionErrorState = submissionStatus?.error;
	const isFormErrorState = Object.keys(errors ?? {}).length > 0;

	const icon = isSubmittingState ? (
		<CircularProgress size={20} />
	) : isSubmittedState ? (
		<CheckIcon color='success' />
	) : isSubmissionErrorState ? (
		<ErrorIcon color='error' />
	) : isFormErrorState ? (
		<ReportProblemIcon color='warning' />
	) : (
		<PAGE_ICONS.DELEGATE color='secondary' />
	);

	const title = isSubmittingState
		? 'Submitting...'
		: isSubmittedState
		? 'Submitted Successfully'
		: isSubmissionErrorState
		? 'Submission Unsuccessful'
		: isFormErrorState
		? 'Review and Fix'
		: 'Review and Submit';

	const contents = isSubmittingState ? (
		<Typography variant='body2'>
			Please wait while your request is processed.
		</Typography>
	) : isSubmittedState ? (
		<Stack
			direction='column'
			gap={1}
			alignItems={'center'}
			sx={{
				padding: 3,
			}}
		>
			<Button
				variant='contained'
				startIcon={<PAGE_ICONS.HOME />}
				onClick={() => history.push(PAGE_ROUTES.HOME)}
				sx={{
					width: { sm: '100%', md: '75%', lg: '35%' },
					padding: { md: 2 },
				}}
			>
				Back to User Access Review Home Page
			</Button>
			<Button
				variant='contained'
				startIcon={<HomeIcon />}
				onClick={() => history.push('/index.html')}
				sx={{
					width: { sm: '100%', md: '75%', lg: '35%' },
					padding: { md: 2 },
				}}
			>
				Back to IMS Home Page
			</Button>
		</Stack>
	) : isSubmissionErrorState ? (
		<Typography variant='body2'>{submissionStatus?.message}</Typography>
	) : isFormErrorState ? (
		<Typography variant='body2'>
			Please review and fix the following errors:
			<ul>
				{Object.keys(errors).map(key => (
					<li key={key}>
						{DELEGATE_FIELDS[key].reviewLabel}: {errors[key]}
					</li>
				))}
			</ul>
		</Typography>
	) : (
		<Typography variant='body2'>
			Upon submission, you will be delegating privileges to{' '}
			{values[DELEGATE_FIELD_KEYS.DELEGATEE][0]?.name} from{' '}
			{values[DELEGATE_FIELD_KEYS.START_DATE]} to{' '}
			{values[DELEGATE_FIELD_KEYS.END_DATE]} for the following:
			<ul>
				{values[DELEGATE_FIELD_KEYS.USERS_LIST].map(user => (
					<li key={user?.fiId}>{user?.name}</li>
				))}
			</ul>
		</Typography>
	);
	return (
		<ReviewAndSubmitView icon={icon} title={title} contents={contents} />
	);
};

ReviewAndSubmit.propTypes = {
	submissionStatus: PropTypes.object,
};

export default ReviewAndSubmit;
